/**
 * Siri Shortcuts utility (iOS only).
 *
 * All exports are safe to call on Android — they are no-ops on non-iOS platforms.
 * The react-native-siri-shortcut package is conditionally required so it never
 * initialises on Android where the native module doesn't exist.
 *
 * Activity types must match NSUserActivityTypes in app.json exactly.
 */

import { Platform } from 'react-native';

// ── Activity type identifiers ──────────────────────────────────────────────

export const SHORTCUT_TYPES = {
  NEW_NOTE:          'com.eleplan.app.new-note',
  NEW_VOICE_NOTE:    'com.eleplan.app.new-voice-note',
  OPEN_CHAT:         'com.eleplan.app.open-chat',
  OPEN_PROJECT_CHAT: 'com.eleplan.app.open-project-chat',
  NEW_MEDICATION:    'com.eleplan.app.new-medication',
  OPEN_BENEFITS:     'com.eleplan.app.open-benefits',
  OPEN_CALENDAR:     'com.eleplan.app.open-calendar',
  OPEN_CONTACTS:     'com.eleplan.app.open-contacts',
} as const;

export type ShortcutType = typeof SHORTCUT_TYPES[keyof typeof SHORTCUT_TYPES];

export interface ShortcutUserInfo {
  projectId?: number;
  projectName?: string;
  action?: string;
}

export interface ExistingSiriShortcut {
  identifier?: string;
  phrase?: string;
  options?: {
    activityType?: string;
    persistentIdentifier?: string;
    userInfo?: {
      projectId?: number;
      projectName?: string;
      action?: string;
    };
  };
}

export interface SiriShortcutEventPayload {
  activityType: string;
  userInfo?: {
    projectId?: number | string;
    projectName?: string;
    action?: string;
    [key: string]: any;
  };
}

export interface SiriShortcutSubscription {
  remove: () => void;
}

export interface SiriShortcutOptions {
  activityType: string;
  title?: string;
  userInfo?: ShortcutUserInfo;
  keywords?: string[];
  persistentIdentifier?: string;
  isEligibleForSearch?: boolean;
  isEligibleForPrediction?: boolean;
  suggestedInvocationPhrase?: string;
}

// ── Template definitions shown on the shortcuts page ──────────────────────

export interface ShortcutTemplate {
  activityType: ShortcutType;
  /** Default Siri phrase suggestion shown to the user */
  suggestedPhrase: (projectName?: string) => string;
  title: (projectName?: string) => string;
  description: (projectName?: string) => string;
  icon: string;
  iconColor: string;
  /** If true this shortcut requires a project to be selected */
  requiresProject: boolean;
}

export const SHORTCUT_TEMPLATES: ShortcutTemplate[] = [
  {
    activityType: SHORTCUT_TYPES.OPEN_CHAT,
    suggestedPhrase: () => 'Open Eleplan chat',
    title: () => 'Open AI chat',
    description: () => 'Opens the account-level AI assistant',
    icon: 'sparkles',
    iconColor: '#6366f1',
    requiresProject: false,
  },
  {
    activityType: SHORTCUT_TYPES.OPEN_PROJECT_CHAT,
    suggestedPhrase: (p) => `Chat about ${p ?? 'my plan'}`,
    title: (p) => `Chat about ${p ?? 'plan'}`,
    description: (p) => `Opens the AI chat for ${p ?? 'this plan'}`,
    icon: 'message-lines',
    iconColor: '#6366f1',
    requiresProject: true,
  },
  {
    activityType: SHORTCUT_TYPES.NEW_NOTE,
    suggestedPhrase: (p) => `New note for ${p ?? 'my plan'}`,
    title: (p) => `New note${p ? ` for ${p}` : ''}`,
    description: () => 'Opens the notes screen ready to create a new text note',
    icon: 'note-sticky',
    iconColor: '#3b82f6',
    requiresProject: true,
  },
  {
    activityType: SHORTCUT_TYPES.NEW_VOICE_NOTE,
    suggestedPhrase: (p) => `Voice note for ${p ?? 'my plan'}`,
    title: (p) => `New voice note${p ? ` for ${p}` : ''}`,
    description: () => 'Opens the voice recorder in the notes screen',
    icon: 'microphone',
    iconColor: '#ef4444',
    requiresProject: true,
  },
  {
    activityType: SHORTCUT_TYPES.NEW_MEDICATION,
    suggestedPhrase: (p) => `Add medication for ${p ?? 'my plan'}`,
    title: (p) => `Add medication${p ? ` for ${p}` : ''}`,
    description: () => 'Opens the medications screen',
    icon: 'pills',
    iconColor: '#10b981',
    requiresProject: true,
  },
  {
    activityType: SHORTCUT_TYPES.OPEN_BENEFITS,
    suggestedPhrase: (p) => `Open benefits for ${p ?? 'my plan'}`,
    title: (p) => `View benefits${p ? ` for ${p}` : ''}`,
    description: () => 'Opens the benefits eligibility screen',
    icon: 'hand-holding-heart',
    iconColor: '#f59e0b',
    requiresProject: true,
  },
  {
    activityType: SHORTCUT_TYPES.OPEN_CALENDAR,
    suggestedPhrase: (p) => `Open calendar for ${p ?? 'my plan'}`,
    title: (p) => `View calendar${p ? ` for ${p}` : ''}`,
    description: () => 'Opens the care calendar',
    icon: 'calendar-days',
    iconColor: '#8b5cf6',
    requiresProject: true,
  },
  {
    activityType: SHORTCUT_TYPES.OPEN_CONTACTS,
    suggestedPhrase: (p) => `Open contacts for ${p ?? 'my plan'}`,
    title: (p) => `View contacts${p ? ` for ${p}` : ''}`,
    description: () => 'Opens the care team contacts list',
    icon: 'address-book',
    iconColor: '#0ea5e9',
    requiresProject: true,
  },
];

// ── Lazy-load the native module (iOS only) ────────────────────────────────

function getModule(): any | null {
  if (Platform.OS !== 'ios') return null;
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    return require('react-native-siri-shortcut').default;
  } catch {
    return null;
  }
}

// ── Public helpers ─────────────────────────────────────────────────────────

/**
 * Donate a shortcut to Siri after the user performs an action.
 * Siri learns from repeated donations to suggest the shortcut proactively.
 */
export function donateShortcut(
  activityType: ShortcutType,
  userInfo: ShortcutUserInfo = {},
): void {
  const mod = getModule();
  if (!mod) return;
  const template = SHORTCUT_TEMPLATES.find((t) => t.activityType === activityType);
  if (!template) return;
  const projectName = userInfo.projectName;
  try {
    const requiredUserInfoKeys = Object.keys(userInfo);
    mod.donateShortcut({
      activityType,
      title: template.title(projectName),
      userInfo,
      requiredUserInfoKeys,
      keywords: ['eleplan', 'care', projectName].filter(Boolean) as string[],
      persistentIdentifier: projectName
        ? `${activityType}:${userInfo.projectId}`
        : activityType,
      isEligibleForSearch: true,
      isEligibleForPrediction: true,
      suggestedInvocationPhrase: template.suggestedPhrase(projectName),
    });
  } catch {
    // Non-critical — Siri donation failure should never surface to the user
  }
}

/**
 * Get the SiriShortcutsEvent emitter for listening to shortcut activations.
 * Returns null on Android.
 * Correct event name: 'SiriShortcutListener'
 */
export function getSiriShortcutsEvent(): any | null {
  if (Platform.OS !== 'ios') return null;
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    return require('react-native-siri-shortcut').SiriShortcutsEvent;
  } catch {
    return null;
  }
}

/**
 * Preferred listener API from react-native-siri-shortcut docs.
 * Falls back to legacy event emitter when addShortcutListener isn't present.
 */
export function addSiriShortcutListener(
  callback: (event: SiriShortcutEventPayload) => void,
): SiriShortcutSubscription | null {
  if (Platform.OS !== 'ios') return null;
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const siri = require('react-native-siri-shortcut');
    if (typeof siri?.addShortcutListener === 'function') {
      return siri.addShortcutListener(callback);
    }
    if (siri?.SiriShortcutsEvent?.addListener) {
      return siri.SiriShortcutsEvent.addListener('SiriShortcutListener', callback);
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Get the Siri shortcut that launched the app from a terminated state.
 * Returns null when there is no initial shortcut.
 */
export async function getInitialSiriShortcut(): Promise<SiriShortcutEventPayload | null> {
  if (Platform.OS !== 'ios') return null;
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const siri = require('react-native-siri-shortcut');
    if (!siri?.getInitialShortcut) return null;
    const shortcut = await siri.getInitialShortcut();
    if (!shortcut || typeof shortcut.activityType !== 'string' || shortcut.activityType.length === 0) {
      return null;
    }
    return {
      activityType: shortcut.activityType,
      userInfo: shortcut.userInfo ?? {},
    };
  } catch {
    return null;
  }
}

/**
 * Get the SiriShortcutButton native component.
 * Returns null on Android (callers must guard with Platform.OS === 'ios').
 */
export function getSiriShortcutButton(): React.ComponentType<any> | null {
  if (Platform.OS !== 'ios') return null;
  try {
    // react-native-siri-shortcut v3 exports AddToSiriButton.
    // Keep SiriShortcutButton fallback for backward compatibility.
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const siri = require('react-native-siri-shortcut');
    return siri.AddToSiriButton ?? siri.SiriShortcutButton ?? null;
  } catch {
    return null;
  }
}

/**
 * Get Siri button style constants from the native module.
 * Returns null on Android.
 */
export function getSiriButtonStyles(): Record<string, number> | null {
  if (Platform.OS !== 'ios') return null;
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    return require('react-native-siri-shortcut').SiriButtonStyles ?? null;
  } catch {
    return null;
  }
}

/**
 * Read currently configured Siri shortcuts from iOS.
 * Returns an empty list when unavailable or on Android.
 */
export async function getExistingSiriShortcuts(): Promise<ExistingSiriShortcut[]> {
  if (Platform.OS !== 'ios') return [];
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const siri = require('react-native-siri-shortcut');
    if (!siri?.getShortcuts) return [];
    const shortcuts = await siri.getShortcuts();
    return Array.isArray(shortcuts) ? shortcuts : [];
  } catch {
    return [];
  }
}

export type PresentShortcutStatus =
  | 'cancelled'
  | 'added'
  | 'deleted'
  | 'updated'
  | 'unsupported'
  | 'error';

/**
 * Open the iOS "Add to Siri" sheet for a shortcut.
 */
export async function presentSiriShortcut(
  options: SiriShortcutOptions,
): Promise<PresentShortcutStatus> {
  if (Platform.OS !== 'ios') return 'unsupported';
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const siri = require('react-native-siri-shortcut');
    if (!siri?.supportsPresentShortcut || !siri?.presentShortcut) {
      return 'unsupported';
    }

    return await new Promise<PresentShortcutStatus>((resolve) => {
      siri.presentShortcut(options, (result: { status?: PresentShortcutStatus }) => {
        resolve(result?.status ?? 'cancelled');
      });
    });
  } catch {
    return 'error';
  }
}
